import { INFRASTRUCTURE_TARGETS } from "./combat-types.js";
import { decodeDiplomacyIntent, isDiplomacyKind } from "./diplomacy-protocol.js";
import { UNIT_IDS } from "./logistics-types.js";
import { QUEUE_KINDS, type QueueKind } from "./manufacturing-types.js";
import { decodeReinforcementIntent } from "./reinforcement-protocol.js";
import type { ConflictIntent, ControllerEnvelope } from "./types.js";

const ENVELOPE_FIELDS = [
  "matchId",
  "seatId",
  "authorityEpoch",
  "controllerGeneration",
  "controllerSequence",
  "requestId",
  "expectedScopeGeneration",
  "intent",
] as const;

export function decodeControllerEnvelope(value: unknown): ControllerEnvelope {
  const source = object(value, "Controller envelope");
  exactFields(source, ENVELOPE_FIELDS, "Controller envelope");
  return Object.freeze({
    matchId: text(source.matchId, "matchId"),
    seatId: text(source.seatId, "seatId"),
    authorityEpoch: positiveInteger(source.authorityEpoch, "authorityEpoch"),
    controllerGeneration: nonnegativeInteger(source.controllerGeneration, "controllerGeneration"),
    controllerSequence: positiveInteger(source.controllerSequence, "controllerSequence"),
    requestId: text(source.requestId, "requestId"),
    expectedScopeGeneration: unsignedIntegerText(
      source.expectedScopeGeneration,
      "expectedScopeGeneration",
    ),
    intent: decodeIntent(source.intent),
  });
}

function decodeIntent(value: unknown): ConflictIntent {
  const source = object(value, "Intent");
  const kind = text(source.kind, "intent kind");
  if (
    [
      "route-production",
      "allocate-army",
      "adjust-army",
      "set-dispatch-mode",
      "launch-wave",
      "release-wave",
      "set-wave-interval",
    ].includes(kind)
  )
    return decodeReinforcementIntent(source);
  if (kind === "set-power") {
    exactFields(
      source,
      ["kind", "industry", "defenses", "militaryProduction", "research"],
      "Power intent",
    );
    return Object.freeze({
      kind,
      industry: finite(source.industry, "industry"),
      defenses: finite(source.defenses, "defenses"),
      militaryProduction: finite(source.militaryProduction, "militaryProduction"),
      research: finite(source.research, "research"),
    });
  }
  if (kind === "set-factory-works") {
    exactFields(
      source,
      ["kind", "rig-factory", "yard-factory", "robot-factory"],
      "Factory Works intent",
    );
    return Object.freeze({
      kind,
      "rig-factory": finite(source["rig-factory"], "rig-factory"),
      "yard-factory": finite(source["yard-factory"], "yard-factory"),
      "robot-factory": finite(source["robot-factory"], "robot-factory"),
    });
  }
  if (kind === "set-metal-priority") {
    exactFields(source, ["kind", "priority"], "Metal priority intent");
    if (!Array.isArray(source.priority)) throw new TypeError("priority is invalid");
    return Object.freeze({
      kind,
      priority: Object.freeze(source.priority.map((entry) => text(entry, "priority lane"))),
    }) as ConflictIntent;
  }
  if (kind === "set-power-priority") {
    exactFields(source, ["kind", "priority"], "Power priority intent");
    if (!Array.isArray(source.priority)) throw new TypeError("priority is invalid");
    return Object.freeze({
      kind,
      priority: Object.freeze(source.priority.map((entry) => text(entry, "priority department"))),
    }) as ConflictIntent;
  }
  if (isManufacturingKind(kind)) {
    return decodeManufacturingIntent(kind, source);
  }
  if (isLogisticsKind(kind)) return decodeLogisticsIntent(kind, source);
  if (kind === "set-target") {
    exactFields(source, ["kind", "targetSeatId"], "Target intent");
    return Object.freeze({ kind, targetSeatId: text(source.targetSeatId, "targetSeatId") });
  }
  if (isDiplomacyKind(kind)) return decodeDiplomacyIntent(kind, source, text, exactFields);
  if (kind === "surrender") {
    exactFields(source, ["kind", "confirmed"], "Surrender intent");
    if (source.confirmed !== true) throw new TypeError("Surrender must be confirmed");
    return Object.freeze({ kind, confirmed: true });
  }
  throw new TypeError(`Unknown intent kind: ${kind}`);
}

function isManufacturingKind(kind: string): boolean {
  return [
    "enqueue-queue-order",
    "cancel-queue-order",
    "reorder-queue",
    "set-queue-repeat",
    "set-queue-paused",
    "enqueue-research",
    "cancel-research",
    "set-construction-split",
    "emergency-restoration-preset",
  ].includes(kind);
}

function isLogisticsKind(kind: string): boolean {
  return (
    kind.includes("troops") ||
    kind.includes("transit") ||
    kind.includes("standing") ||
    kind.includes("supply") ||
    kind.includes("auto-reinforce") ||
    kind === "set-shields" ||
    kind === "set-infrastructure-target"
  );
}

function decodeLogisticsIntent(kind: string, source: Record<string, unknown>): ConflictIntent {
  if (kind === "set-supply" || kind === "set-shields") {
    exactFields(source, ["kind", "allocations"], "Supply intent");
    return { kind, allocations: numberRecord(source.allocations, "supply allocations") };
  }
  if (kind === "set-infrastructure-target") {
    exactFields(source, ["kind", "borderId", "target"], "Infrastructure target intent");
    const target = text(source.target, "target");
    if (!INFRASTRUCTURE_TARGETS.includes(target as never)) throw new TypeError("target is invalid");
    return { kind, borderId: text(source.borderId, "borderId"), target: target as never };
  }
  if (kind === "deploy-troops") {
    exactFields(source, ["kind", "destination", "units"], "Deploy intent");
    return {
      kind,
      destination: text(source.destination, "destination"),
      units: troops(source.units),
    };
  }
  if (kind === "return-troops") {
    exactFields(source, ["kind", "origin", "units"], "Return intent");
    return { kind, origin: text(source.origin, "origin"), units: troops(source.units) };
  }
  if (kind === "redeploy-troops") {
    exactFields(source, ["kind", "origin", "destination", "units"], "Redeploy intent");
    return {
      kind,
      origin: text(source.origin, "origin"),
      destination: text(source.destination, "destination"),
      units: troops(source.units),
    };
  }
  if (kind === "cancel-transit" || kind === "cancel-auto-reinforce") {
    exactFields(source, ["kind", "orderId"], "Cancel logistics intent");
    return { kind, orderId: text(source.orderId, "orderId") };
  }
  if (kind === "set-standing-order") {
    exactFields(source, ["kind", "borderId", "order"], "Standing order intent");
    const order = text(source.order, "order");
    if (!["hold", "assault", "raid"].includes(order)) throw new TypeError("order is invalid");
    return { kind, borderId: text(source.borderId, "borderId"), order: order as never };
  }
  if (kind === "enqueue-auto-reinforce") {
    exactFields(source, ["kind", "destination", "desired"], "Auto-reinforce intent");
    return {
      kind,
      destination: text(source.destination, "destination"),
      desired: desiredTroops(source.desired),
    };
  }
  if (kind === "reorder-auto-reinforce") {
    exactFields(source, ["kind", "orderIds"], "Auto-reinforce reorder intent");
    if (!Array.isArray(source.orderIds)) throw new TypeError("orderIds is invalid");
    return { kind, orderIds: source.orderIds.map((id) => text(id, "orderId")) };
  }
  throw new TypeError(`Unknown intent kind: ${kind}`);
}

function decodeManufacturingIntent(kind: string, source: Record<string, unknown>): ConflictIntent {
  if (kind === "set-construction-split") {
    exactFields(source, ["kind", "newConstruction", "restoration"], "Construction split intent");
    return {
      kind,
      newConstruction: finite(source.newConstruction, "newConstruction"),
      restoration: finite(source.restoration, "restoration"),
    };
  }
  if (kind === "enqueue-queue-order") {
    exactFields(source, ["kind", "queue", "target", "quantity", "repeat"], "Enqueue intent");
    return {
      kind,
      queue: queueKind(source.queue),
      target: text(source.target, "target") as never,
      quantity: text(source.quantity, "quantity"),
      repeat: boolean(source.repeat, "repeat"),
    };
  }
  if (kind === "cancel-queue-order") {
    exactFields(source, ["kind", "queue", "orderId"], "Cancel intent");
    return { kind, queue: queueKind(source.queue), orderId: text(source.orderId, "orderId") };
  }
  if (kind === "reorder-queue") {
    exactFields(source, ["kind", "queue", "orderIds"], "Reorder intent");
    if (!Array.isArray(source.orderIds)) throw new TypeError("orderIds is invalid");
    return {
      kind,
      queue: queueKind(source.queue),
      orderIds: Object.freeze(source.orderIds.map((id) => text(id, "orderId"))),
    };
  }
  if (kind === "set-queue-repeat") {
    exactFields(source, ["kind", "queue", "orderId", "repeat"], "Repeat intent");
    return {
      kind,
      queue: queueKind(source.queue),
      orderId: text(source.orderId, "orderId"),
      repeat: boolean(source.repeat, "repeat"),
    };
  }
  if (kind === "set-queue-paused") {
    exactFields(source, ["kind", "queue", "paused"], "Pause intent");
    return { kind, queue: queueKind(source.queue), paused: boolean(source.paused, "paused") };
  }
  if (kind === "emergency-restoration-preset") {
    exactFields(source, ["kind"], "Emergency preset intent");
    return { kind };
  }
  if (kind === "enqueue-research") {
    exactFields(source, ["kind", "researchKind", "target"], "Research enqueue intent");
    const researchKind = text(source.researchKind, "researchKind");
    if (!["technology", "specialization", "repeatable"].includes(researchKind))
      throw new TypeError("researchKind is invalid");
    return { kind, researchKind: researchKind as never, target: text(source.target, "target") };
  }
  if (kind === "cancel-research") {
    exactFields(source, ["kind", "orderId"], "Research cancel intent");
    return { kind, orderId: text(source.orderId, "orderId") };
  }
  throw new TypeError(`Unknown intent kind: ${kind}`);
}

function object(value: unknown, label: string): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new TypeError(`${label} must be an object`);
  }
  return value as Record<string, unknown>;
}

function troops(value: unknown) {
  const source = object(value, "troops");
  exactFields(source, UNIT_IDS, "Troops");
  return Object.freeze(
    Object.fromEntries(UNIT_IDS.map((id) => [id, text(source[id], id)])),
  ) as never;
}

function desiredTroops(value: unknown) {
  const source = object(value, "desired troops");
  exactFields(source, UNIT_IDS, "Desired troops");
  return Object.freeze(
    Object.fromEntries(
      UNIT_IDS.map((id) => [id, source[id] === null ? null : text(source[id], id)]),
    ),
  ) as never;
}

function numberRecord(value: unknown, label: string): Readonly<Record<string, number>> {
  const source = object(value, label);
  return Object.freeze(
    Object.fromEntries(Object.entries(source).map(([id, amount]) => [id, finite(amount, id)])),
  );
}

function exactFields(
  value: Record<string, unknown>,
  fields: readonly string[],
  label: string,
): void {
  const expected = new Set(fields);
  if (
    Object.keys(value).length !== fields.length ||
    Object.keys(value).some((key) => !expected.has(key))
  ) {
    throw new TypeError(`${label} has unknown or missing fields`);
  }
}

function text(value: unknown, label: string): string {
  if (typeof value !== "string" || value.length === 0 || value.length > 128) {
    throw new TypeError(`${label} must be nonempty bounded text`);
  }
  return value;
}

function finite(value: unknown, label: string): number {
  if (typeof value !== "number" || !Number.isFinite(value))
    throw new TypeError(`${label} is invalid`);
  return value;
}

function boolean(value: unknown, label: string): boolean {
  if (typeof value !== "boolean") throw new TypeError(`${label} is invalid`);
  return value;
}

function queueKind(value: unknown): QueueKind {
  const result = text(value, "queue");
  if (!QUEUE_KINDS.includes(result as QueueKind)) throw new TypeError("queue is invalid");
  return result as QueueKind;
}

function positiveInteger(value: unknown, label: string): number {
  const result = nonnegativeInteger(value, label);
  if (result < 1) throw new TypeError(`${label} must be positive`);
  return result;
}

function nonnegativeInteger(value: unknown, label: string): number {
  if (!Number.isSafeInteger(value) || (value as number) < 0) {
    throw new TypeError(`${label} must be a nonnegative safe integer`);
  }
  return value as number;
}

function unsignedIntegerText(value: unknown, label: string): string {
  const result = text(value, label);
  if (!/^(0|[1-9][0-9]*)$/.test(result))
    throw new TypeError(`${label} must be an unsigned integer`);
  return result;
}
